import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute enforces authentication.
 * Users with no subscription are treated as free tier and have full access.
 * Flow:
 * 1. If auth not initialized yet -> show lightweight loading state.
 * 2. If no accessToken (not logged in) -> redirect to /login.
 * 3. Otherwise, allow access.
 *
 * Supports two usage patterns:
 *   1) Wrapper:  <ProtectedRoute><AppLayout /></ProtectedRoute>
 *   2) Layout:   <Route element={<ProtectedRoute />}> (renders <Outlet />)
 */
const ProtectedRoute = ({ children }) => {
  const { accessToken, isInitialized } = useAuth();
  const location = useLocation();

  // Basic auth init gate
  if (!isInitialized) {
    return <div>Loading authentication info...</div>; // kept minimal to reduce flicker
  }
  // Not authenticated
  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Subscription gating removed - users with no subscription are treated as free tier
  // and have full access to the application with optional upgrade prompts
  return children || <Outlet />;
};

export default ProtectedRoute;
